import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InstaTopbar from '../InstaTopbar/InstaTopbar';
import useBlockCheck from '../../hooks/useBlockCheck';
import './Performance.css';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { FaStar, FaUsers, FaGraduationCap, FaChartLine, FaDollarSign, FaClock, FaTrophy, FaEye } from 'react-icons/fa';
// Chart imports
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Performance = () => {
  const { token, profile } = useAuth();
  useBlockCheck('instructor');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // New state for enhanced metrics
  const [earnings, setEarnings] = useState({ thisMonth: 0, total: 0 });
  const [courseCompletionRates, setCourseCompletionRates] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [topCourses, setTopCourses] = useState([]);
  const [studentEngagement, setStudentEngagement] = useState({
    activeStudents: 0,
    completionRate: 0,
    averageWatchTime: 0,
    averageWatchTimeFormatted: '0h 0m',
  });
  const [loading, setLoading] = useState(true);

  // Fetch instructor's courses and calculate total students
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        // Fetch published courses
        const { data: coursesData } = await axios.get(`${api.getCourses}?instructor=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Fetch draft courses
        const { data: draftsData } = await axios.get(api.getCourseDrafts, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (coursesData.courses) {
          setCourses(coursesData.courses);
          // Calculate total students across all courses
          const total = coursesData.courses.reduce((sum, course) => sum + (course.enrolledStudents?.length || 0), 0);
          setTotalStudents(total);
        }

        if (draftsData.drafts) {
          setDrafts(draftsData.drafts);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [token]);

  // Fetch instructor's average rating and total ratings
  useEffect(() => {
    const fetchInstructorRatings = async () => {
      try {
        const { data } = await axios.get(api.getInstructorRatings(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data.instructor) {
          setAverageRating(data.instructor.averageRating || 0);
          setTotalRatings(data.instructor.totalRatings || 0);
        }
      } catch (error) {
        console.error('Error fetching instructor ratings:', error);
      }
    };

    if (profile && profile._id) {
      fetchInstructorRatings();
    }
  }, [profile, token]);

  // Fetch instructor's total reviews
  useEffect(() => {
    const fetchInstructorReviews = async () => {
      try {
        const { data } = await axios.get(api.getInstructorReviews(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        setTotalReviews(data.totalReviews || 0);
      } catch (error) {
        console.error('Error fetching instructor reviews:', error);
      }
    };

    if (profile && profile._id) {
      fetchInstructorReviews();
    }
  }, [profile, token]);

  // Fetch instructor total watch time
  useEffect(() => {
    const fetchWatchTimeAndCompletion = async () => {
      if (!token) return;
      try {
        const { data } = await axios.get(api.getInstructorTotalWatchTime, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { totalWatchTimeSeconds, totalCourseDurationSeconds } = data;

        const totalHours = Math.floor(totalWatchTimeSeconds / 3600);
        const totalMinutes = Math.floor((totalWatchTimeSeconds % 3600) / 60);

        // Calculate completion rate
        const completionRate = totalCourseDurationSeconds > 0
          ? Math.round((totalWatchTimeSeconds / totalCourseDurationSeconds) * 100)
          : 0;

        setStudentEngagement(prev => ({
          ...prev,
          averageWatchTimeFormatted: `${totalHours}h ${totalMinutes}m`,
          completionRate: Math.min(100, completionRate), // Cap at 100%
        }));

      } catch (error) {
        console.error('Error fetching watch time and completion data:', error);
      }
    };

    fetchWatchTimeAndCompletion();
  }, [token]);

  // Instead, always use the local calculation for earnings
  useEffect(() => {
    if (courses.length > 0) {
      calculateEarningsFromCourses();
    }
  }, [courses]);

  // Calculate earnings from course data as fallback
  const calculateEarningsFromCourses = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let thisMonthEarnings = 0;
    let totalEarnings = 0;

    courses.forEach(course => {
      if (course.enrolledStudents && course.enrolledStudents.length > 0) {
        course.enrolledStudents.forEach(student => {
          const coursePrice = course.isFree ? 0 : (course.offerPrice || course.actualPrice || 0);
          // Show full earnings without platform cut
          const fullEarnings = coursePrice;

          totalEarnings += fullEarnings;

          if (student.purchasedDate) {
            const purchaseDate = new Date(student.purchasedDate);
            const purchaseMonth = purchaseDate.getMonth();
            const purchaseYear = purchaseDate.getFullYear();

            if (purchaseMonth === currentMonth && purchaseYear === currentYear) {
              thisMonthEarnings += fullEarnings;
            }
          }
        });
      }
    });

    setEarnings({
      thisMonth: thisMonthEarnings,
      total: totalEarnings,
    });
  };

  // Fetch course-specific metrics like individual completion rates
  useEffect(() => {
    const fetchCourseSpecificMetrics = async () => {
      if (!token || courses.length === 0) return;
      try {
        const { data } = await axios.get(api.getInstructorCourseCompletionRates, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data.completionRates) {
          setCourseCompletionRates(data.completionRates);

          // Recalculate top performing courses with real data
          const coursesWithMetrics = courses.map(course => ({
            ...course,
            completionRate: data.completionRates[course._id] || 0,
            studentCount: course.enrolledStudents?.length || 0,
            rating: course.averageRating || 0,
          }));

          const sortedCourses = coursesWithMetrics
            .sort((a, b) => (b.rating * b.studentCount + b.completionRate) - (a.rating * a.studentCount + a.completionRate))
            .slice(0, 3);

          setTopCourses(sortedCourses);
        }
      } catch (error) {
        console.error('Error fetching course-specific metrics:', error);
      }
    };

    fetchCourseSpecificMetrics();
  }, [token, courses]);

  // Calculate active students and recent activity from courses
  useEffect(() => {
    if (courses.length === 0) return;

    // Calculate active students
    const totalActiveStudents = courses.reduce((sum, course) => sum + (course.enrolledStudents?.length || 0), 0);
    setStudentEngagement(prev => ({
      ...prev,
      activeStudents: totalActiveStudents,
    }));

    // Generate recent activity
    const activity = [];
    courses.forEach(course => {
      if (course.enrolledStudents) {
        course.enrolledStudents.forEach(student => {
          if (student.purchasedDate) {
            activity.push({
              type: 'enrollment',
              courseTitle: course.title,
              studentName: student.name || student.email,
              date: new Date(student.purchasedDate),
              courseId: course._id,
            });
          }
        });
      }
    });

    const sortedActivity = activity
      .sort((a, b) => b.date - a.date)
      .slice(0, 5);

    setRecentActivity(sortedActivity);
  }, [courses]);

  // Calculate course counts dynamically
  const publishedCount = courses.length;
  const unpublishedCount = drafts.filter(draft => draft.published === false).length;
  const totalCourses = publishedCount + unpublishedCount;

  // Calculate average completion rate across all courses
  const courseIds = Object.keys(courseCompletionRates);
  const averageCompletionRate = courseIds.length > 0
    ? Math.round(
      courseIds.reduce((sum, id) => sum + (courseCompletionRates[id] || 0), 0) / courseIds.length,
    )
    : 0;

  const handleTotalCoursesClick = () => {
    setShowCourseModal(true);
  };

  const handleClosePerformanceModal = () => {
    navigate('/dashboard');
  };

  const handleCloseCourseModal = () => {
    setShowCourseModal(false);
  };

  const handleNavigate = (filter) => {
    if (filter === 'students') {
      navigate('/total-students');
    } else {
      navigate(`/my-courses?filter=${filter}`);
    }
    setShowCourseModal(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Chart data for Performance Overview
  const chartData = {
    labels: [
      'Total Students',
      'Total Courses',
      'Avg. Rating',
      'Total Earnings',
      'Completion Rate',
      'Reviews',
    ],
    datasets: [
      {
        label: 'Performance Metrics',
        data: [
          totalStudents,
          publishedCount + unpublishedCount,
          averageRating,
          earnings.total,
          averageCompletionRate,
          totalReviews,
        ],
        backgroundColor: [
          '#007bff',
          '#28a745',
          '#ffc107',
          '#17a2b8',
          '#6f42c1',
          '#fd7e14',
        ],
        borderRadius: 8,
        maxBarThickness: 50,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Performance Overview (Summary)',
        font: { size: 18 },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (context.dataIndex === 3) {
              // Earnings
              return `Total Earnings: ₹${context.parsed.y.toLocaleString()}`;
            }
            if (context.dataIndex === 2) {
              // Rating
              return `Avg. Rating: ${context.parsed.y}/5`;
            }
            if (context.dataIndex === 4) {
              // Completion Rate
              return `Completion Rate: ${context.parsed.y}%`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const renderContent = () => {
    switch (activeTab) {
    case 'overview':
      return (
        <>
          <h1>Performance Overview</h1>
          <button
            className="p-modal-close"
            onClick={() => navigate('/dashboard')}
          >
              ×
          </button>
          <p>Here you can view your course performance and user analytics.</p>
          {/* Main Stats Grid */}
          <div className="stats-grid">
            <div
              className="stats-card"
              onClick={() => handleNavigate('students')}
            >
              <div className="stats-icon">
                <FaUsers />
              </div>
              <h2>Total Students</h2>
              <p>{totalStudents}</p>
            </div>

            <div className="stats-card" onClick={handleTotalCoursesClick}>
              <div className="stats-icon">
                <FaGraduationCap />
              </div>
              <h2>Total Courses</h2>
              <p>{totalCourses}</p>
            </div>

            <div className="stats-card">
              <div className="stats-icon">
                <FaStar />
              </div>
              <h2>Average Rating</h2>
              <p>
                {averageRating.toFixed(1)}/5 ({totalRatings} ratings)
              </p>
            </div>

            <div className="stats-card">
              <div className="stats-icon" style={{ fontSize: '28px' }}>
                  ₹
              </div>
              <h2>Total Earnings</h2>
              <p>{formatCurrency(earnings.total)}</p>
            </div>
          </div>

          {/* Secondary Metrics Grid */}
          <div className="secondary-stats-grid">
            <div className="secondary-stats-card">
              <div className="stats-icon" style={{ fontSize: '28px' }}>
                  ₹
              </div>
              <h3>This Month's Earnings</h3>
              <p>{formatCurrency(earnings.thisMonth)}</p>
            </div>

            <div className="secondary-stats-card">
              <div className="stats-icon">
                <FaEye />
              </div>
              <h3>Active Students</h3>
              <p>{studentEngagement.activeStudents}</p>
            </div>

            <div className="secondary-stats-card">
              <div className="stats-icon">
                <FaClock />
              </div>
              <h3>Total Watch Time</h3>
              <p>{studentEngagement.averageWatchTimeFormatted}</p>
            </div>

            <div className="secondary-stats-card">
              <div className="stats-icon">
                <FaTrophy />
              </div>
              <h3>Completion Rate</h3>
              <p>{averageCompletionRate}%</p>
            </div>
          </div>

          {/* Top Performing Courses */}
          {topCourses.length > 0 && (
            <div className="top-courses-section">
              <h2>Top Performing Courses</h2>
              <div className="top-courses-grid">
                {topCourses.map((course, index) => (
                  <div key={course._id} className="top-course-card">
                    <div className="course-rank">#{index + 1}</div>
                    <h3 className="pf-course-title">{course.title}</h3>
                    <div className="course-metrics">
                      <span>Students: {course.studentCount}</span>
                      <span>Rating: {course.rating.toFixed(1)}/5</span>
                      <span>Completion: {course.completionRate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <div className="recent-activity-section">
              <h2>Recent Activity</h2>
              <div className="activity-list">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      <FaUsers />
                    </div>
                    <div className="activity-content">
                      <p>
                        <strong>{activity.studentName}</strong> enrolled in{' '}
                        <strong>{activity.courseTitle}</strong>
                      </p>
                      <span className="activity-date">
                        {formatDate(activity.date)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Course Completion Rates */}
          {Object.keys(courseCompletionRates).length > 0 && (
            <div className="completion-rates-section">
              <h2>Course Completion Rates</h2>
              <div className="completion-rates-grid">
                {courses.map((course) => (
                  <div key={course._id} className="completion-rate-card">
                    <h3 className='pf-course-title'>{course.title}</h3>
                    <div className="completion-bar">
                      <div
                        className="completion-fill"
                        style={{
                          width: `${courseCompletionRates[course._id] || 0}%`,
                        }}
                      ></div>
                    </div>
                    <p>{courseCompletionRates[course._id] || 0}% Complete</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      );
    case 'graph':
      return (
        <>
          <h1>Performance Graph</h1>
          <button className="p-modal-close" onClick={() => navigate('/dashboard')}>
              ×
          </button>
          <p>Visual summary of your key performance metrics.</p>
          <div className="performance-graph-section">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </>
      );
    default:
      return null;
    }
  };

  if (loading) {
    return (
      <div>
        <InstaTopbar />
        <div className="performance-container">
          <div className="page-container-po">
            <div className="loading-spinner">Loading performance data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <InstaTopbar />

      <div className="performance-container">
        <div className="page-container-po">
          {/* Tab Navigation */}
          <div className="performance-tabs">
            <button
              className={activeTab === 'overview' ? 'tab-active' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={activeTab === 'graph' ? 'tab-active' : ''}
              onClick={() => setActiveTab('graph')}
            >
              Graph
            </button>
          </div>
          {renderContent()}
        </div>
      </div>

      {/* Total Courses Modal */}
      {showCourseModal && (
        <div className="modal-overlay" onClick={handleCloseCourseModal}>
          <div className="performance-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Total Courses</h2>
            <button className="course-modal-close-button" onClick={handleCloseCourseModal}>
              ×
            </button>
            <div className="modal-details">
              <p>Published: {publishedCount}</p>
              <p>Unpublished: {unpublishedCount}</p>
            </div>
            <div className="modal-buttons">
              <button onClick={() => handleNavigate('published')}>
                View Published Courses
              </button>
              <button onClick={() => handleNavigate('unpublished')}>
                View Unpublished Courses
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Performance;
